import { createAction, ActionType, getType } from 'typesafe-actions';
import { Dispatch } from 'redux';
import { Task } from '../interfaces/Task';
import { CreateTaskDto, UpdateTaskStatusDto, UpdateTaskDueDateDto } from '../interfaces/dto/TaskDto';
import * as TaskAPI from '../client/task';

// Action Types
export const taskActions = {
    // Fetch tasks actions
    fetchTasksRequest: createAction('FETCH_TASKS_REQUEST')(),
    fetchTasksSuccess: createAction('FETCH_TASKS_SUCCESS')<Task[]>(),
    fetchTasksFailure: createAction('FETCH_TASKS_FAILURE')<string>(),
    
    // Create task actions
    createTaskRequest: createAction('CREATE_TASK_REQUEST')(),
    createTaskSuccess: createAction('CREATE_TASK_SUCCESS')<Task>(),
    createTaskFailure: createAction('CREATE_TASK_FAILURE')<string>(),
    
    // Update task status actions
    updateTaskStatusRequest: createAction('UPDATE_TASK_STATUS_REQUEST')(),
    updateTaskStatusSuccess: createAction('UPDATE_TASK_STATUS_SUCCESS')<Task>(),
    updateTaskStatusFailure: createAction('UPDATE_TASK_STATUS_FAILURE')<string>(),
    
    // Update task due date actions
    updateTaskDueDateRequest: createAction('UPDATE_TASK_DUE_DATE_REQUEST')(),
    updateTaskDueDateSuccess: createAction('UPDATE_TASK_DUE_DATE_SUCCESS')<Task>(),
    updateTaskDueDateFailure: createAction('UPDATE_TASK_DUE_DATE_FAILURE')<string>(),
    
    // Delete task actions
    deleteTaskRequest: createAction('DELETE_TASK_REQUEST')(),
    deleteTaskSuccess: createAction('DELETE_TASK_SUCCESS')<number>(),
    deleteTaskFailure: createAction('DELETE_TASK_FAILURE')<string>(),
    
    // Clear tasks
    clearTasks: createAction('CLEAR_TASKS')(),
    
    // Set selected task
    setSelectedTask: createAction('SET_SELECTED_TASK')<Task | null>(),
};

export type TaskAction = ActionType<typeof taskActions>;

// Task State
export interface TaskState {
    tasks: Task[];
    selectedTask: Task | null;
    isLoading: boolean;
    error: string | null;
}

export const initialTaskState: TaskState = {
    tasks: [],
    selectedTask: null,
    isLoading: false,
    error: null,
};

// Reducer
export const taskReducer = (state = initialTaskState, action: TaskAction): TaskState => {
    switch (action.type) {
        case getType(taskActions.fetchTasksRequest):
        case getType(taskActions.createTaskRequest):
        case getType(taskActions.updateTaskStatusRequest):
        case getType(taskActions.updateTaskDueDateRequest):
        case getType(taskActions.deleteTaskRequest):
            return {
                ...state,
                isLoading: true,
                error: null,
            };
            
        case getType(taskActions.fetchTasksSuccess):
            return {
                ...state,
                isLoading: false,
                tasks: action.payload,
                error: null,
            };
            
        case getType(taskActions.createTaskSuccess):
            return {
                ...state,
                isLoading: false,
                tasks: [...state.tasks, action.payload],
                error: null,
            };
            
        case getType(taskActions.updateTaskStatusSuccess):
        case getType(taskActions.updateTaskDueDateSuccess):
            return {
                ...state,
                isLoading: false,
                tasks: state.tasks.map(task => 
                    task.id === action.payload.id ? action.payload : task
                ),
                selectedTask: state.selectedTask?.id === action.payload.id ? action.payload : state.selectedTask,
                error: null,
            };
            
        case getType(taskActions.deleteTaskSuccess):
            return {
                ...state,
                isLoading: false,
                tasks: state.tasks.filter(task => task.id !== action.payload),
                selectedTask: state.selectedTask?.id === action.payload ? null : state.selectedTask,
                error: null,
            };
            
        case getType(taskActions.fetchTasksFailure):
        case getType(taskActions.createTaskFailure):
        case getType(taskActions.updateTaskStatusFailure):
        case getType(taskActions.updateTaskDueDateFailure):
        case getType(taskActions.deleteTaskFailure):
            return {
                ...state,
                isLoading: false,
                error: action.payload,
            };
            
        case getType(taskActions.clearTasks):
            return {
                ...state,
                tasks: [],
                selectedTask: null,
                error: null,
            };
            
        case getType(taskActions.setSelectedTask):
            return {
                ...state,
                selectedTask: action.payload,
            };
            
        default:
            return state;
    }
};

// Action Creators (Thunks)
export const fetchMyTasks = (projectId?: number) => {
    return (dispatch: Dispatch) => {
        dispatch(taskActions.fetchTasksRequest());
        TaskAPI.getMyTasks(projectId)
            .then(response => {
                dispatch(taskActions.fetchTasksSuccess(response.data));
            })
            .catch(error => {
                const errorMessage = error.response?.data?.message || 'Failed to fetch tasks';
                dispatch(taskActions.fetchTasksFailure(errorMessage));
            });
    };
};

export const createTask = (taskData: CreateTaskDto) => {
    return (dispatch: Dispatch) => {
        dispatch(taskActions.createTaskRequest());
        TaskAPI.createTask(taskData)
            .then(response => {
                dispatch(taskActions.createTaskSuccess(response.data));
            })
            .catch(error => {
                const errorMessage = error.response?.data?.message || 'Failed to create task';
                dispatch(taskActions.createTaskFailure(errorMessage));
            });
    };
};

export const createTaskWithImages = (taskData: CreateTaskDto, images: File[]) => {
    return (dispatch: Dispatch) => {
        dispatch(taskActions.createTaskRequest());
        TaskAPI.createTaskWithImages(taskData, images)
            .then(response => {
                dispatch(taskActions.createTaskSuccess(response.data));
            })
            .catch(error => {
                const errorMessage = error.response?.data?.message || 'Failed to create task with images';
                dispatch(taskActions.createTaskFailure(errorMessage));
            });
    };
};

export const updateTaskStatus = (taskId: number, statusData: UpdateTaskStatusDto) => {
    return (dispatch: Dispatch) => {
        dispatch(taskActions.updateTaskStatusRequest());
        TaskAPI.updateTaskStatus(taskId, statusData)
            .then(response => {
                dispatch(taskActions.updateTaskStatusSuccess(response.data));
            })
            .catch(error => {
                const errorMessage = error.response?.data?.message || 'Failed to update task status';
                dispatch(taskActions.updateTaskStatusFailure(errorMessage));
            });
    };
};

export const updateTaskDueDate = (taskId: number, dueDateData: UpdateTaskDueDateDto) => {
    return (dispatch: Dispatch) => {
        dispatch(taskActions.updateTaskDueDateRequest());
        TaskAPI.updateTaskDueDate(taskId, dueDateData)
            .then(response => {
                dispatch(taskActions.updateTaskDueDateSuccess(response.data));
            })
            .catch(error => {
                const errorMessage = error.response?.data?.message || 'Failed to update task due date';
                dispatch(taskActions.updateTaskDueDateFailure(errorMessage));
            });
    };
};

export const deleteTask = (taskId: number) => {
    return (dispatch: Dispatch) => {
        dispatch(taskActions.deleteTaskRequest());
        TaskAPI.deleteTask(taskId)
            .then(() => {
                dispatch(taskActions.deleteTaskSuccess(taskId));
            })
            .catch(error => {
                const errorMessage = error.response?.data?.message || 'Failed to delete task';
                dispatch(taskActions.deleteTaskFailure(errorMessage));
            });
    };
};
